import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap select-none",
    "font-medium leading-none rounded-md border border-transparent",
    "transition-colors duration-quick ease-out-quad motion-reduce:transition-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-cloverhound-gray-50 dark:focus-visible:ring-offset-cloverhound-gray-950",
    "disabled:opacity-50 disabled:pointer-events-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-cloverhound-indigo-600 text-white",
          "hover:bg-cloverhound-indigo-500 active:bg-cloverhound-indigo-700",
          "dark:bg-cloverhound-indigo-500 dark:hover:bg-cloverhound-indigo-400 dark:active:bg-cloverhound-indigo-600",
        ].join(" "),
        secondary: [
          "bg-white text-cloverhound-gray-900 border-cloverhound-gray-300",
          "hover:bg-cloverhound-gray-100 active:bg-cloverhound-gray-200",
          "dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50 dark:border-cloverhound-gray-800",
          "dark:hover:bg-cloverhound-gray-850 dark:active:bg-cloverhound-gray-700",
        ].join(" "),
        ghost: [
          "bg-transparent text-cloverhound-gray-800",
          "hover:bg-cloverhound-gray-100 active:bg-cloverhound-gray-200",
          "dark:text-cloverhound-gray-300 dark:hover:bg-cloverhound-gray-850 dark:active:bg-cloverhound-gray-800",
        ].join(" "),
        outline: [
          "bg-transparent text-cloverhound-gray-800 border-cloverhound-gray-300",
          "hover:bg-cloverhound-gray-100",
          "dark:text-cloverhound-gray-50 dark:border-cloverhound-gray-700 dark:hover:bg-cloverhound-gray-850",
        ].join(" "),
        destructive: [
          "bg-cloverhound-red-500 text-white",
          "hover:bg-cloverhound-red-700 active:bg-cloverhound-red-700",
        ].join(" "),
        link: [
          "bg-transparent text-cloverhound-indigo-600 underline-offset-4",
          "hover:underline active:text-cloverhound-indigo-700",
          "dark:text-cloverhound-indigo-300",
        ].join(" "),
      },
      size: {
        xs: "h-6 px-2 text-mini gap-1",
        sm: "h-7 px-2.5 text-small",
        md: "h-8 px-3 text-small",
        lg: "h-10 px-4 text-regular",
        icon: "size-8 p-0",
        iconSm: "size-7 p-0",
        iconXs: "size-6 p-0",
      },
      fullWidth: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", fullWidth: false },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  /**
   * When true, render the single child element with button styling merged in.
   * Useful for links-styled-as-buttons: `<Button asChild><Link href="/…">…</Link></Button>`.
   */
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading,
      disabled,
      leadingIcon,
      trailingIcon,
      children,
      type = "button",
      asChild,
      ...props
    },
    ref,
  ) => {
    const content = loading ? (
      <>
        <span className="invisible inline-flex items-center gap-1.5" aria-hidden="true">
          {leadingIcon}
          {children}
          {trailingIcon}
        </span>
        <span aria-hidden="true" className="absolute inset-0 inline-flex items-center justify-center">
          <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        </span>
      </>
    ) : (
      <>
        {leadingIcon}
        {children}
        {trailingIcon}
      </>
    )

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<Record<string, unknown>>
      const childClass =
        typeof (child.props as { className?: unknown }).className === "string"
          ? ((child.props as { className?: string }).className as string)
          : ""
      return React.cloneElement(child, {
        ...(props as Record<string, unknown>),
        ref,
        className: cn(buttonVariants({ variant, size, fullWidth }), className, childClass),
        "aria-busy": loading || undefined,
      } as Record<string, unknown>)
    }

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        {...props}
      >
        {content}
      </button>
    )
  },
)
Button.displayName = "Button"
